import React, { useState, useEffect, useRef, useCallback } from 'react';

import CrmPageLayout from '../../shared/components/CrmPageLayout';
import CrmAvatar from '../../shared/components/CrmAvatar';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import MergeContactsModal from './MergeContactsModal';

import '../styles/duplicatesStyles.css';

const axios = require('axios').default;

const PER_PAGE = 25;

// Chips → what the JSON endpoint groups/filters by (see DUPLICATE_CHIPS server-side).
const CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'likely', label: 'Likely same person' },
  { key: 'same_name', label: 'Same name only' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'card', label: 'Payment cards' },
];

// Setlist tokens — resolve to light/dark automatically under body.od-themed.
const T = {
  textPrimary: 'var(--sl-text-primary)',
  textSecondary: 'var(--sl-text-secondary)',
  textTertiary: 'var(--sl-text-tertiary)',
  brand: 'var(--sl-fg-brand)',
  brandBg: 'var(--sl-bg-brand-primary)',
  success: 'var(--sl-text-success)',
  // Warning tokens back the "medium" (shared-location) confidence pill; fall
  // back to amber so an undefined token never renders as invisible text.
  warning: 'var(--sl-text-warning, #b45309)',
  warningBg: 'var(--sl-bg-warning-primary, #fef3c7)',
  cardBg: 'var(--sl-bg-primary)',
  border: 'var(--sl-border-secondary)',
  activeBg: 'var(--sl-bg-active)',
  successBg: 'var(--sl-bg-success-primary)',
  headerBg: 'var(--sl-bg-secondary)',
  // The --sl-radius-* tokens aren't reliably defined, so fall back like the rest
  // of the app (var(--sl-radius-md, 8px)).
  radius: 'var(--sl-radius-lg, 12px)',
};

function formatCard(card) {
  const brand = card.brand ? `${card.brand.replace(/\b\w/g, (l) => l.toUpperCase())} ` : '';
  return `${brand}••${card.last4}`;
}

// Wrap a value in the blue "match" highlight when it's the group's shared value.
function Highlight({ children, active }) {
  if (!active) return <span style={{ color: T.textSecondary }}>{children}</span>;
  return (
    <span style={{
      background: T.brandBg,
      color: T.brand,
      fontWeight: 600,
      borderRadius: 4,
      padding: '1px 6px',
    }}>
      {children}
    </span>
  );
}

function GroupCard({ group, onMerge }) {
  const { match_type: matchType, match_value: matchValue, confidence, contacts } = group;
  const heading = contacts[0]
    ? ([contacts[0].first_name, contacts[0].last_name].filter(Boolean).join(' ') || contacts[0].email)
    : group.key;

  return (
    <div style={{ background: T.cardBg, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 16, marginBottom: 16 }}>
      <div className="d-flex justify-content-between align-items-start mb-3" style={{ gap: 12 }}>
        <div>
          <span style={{ fontWeight: 600, color: T.textPrimary }}>{heading}</span>
          <span style={{ color: T.textTertiary }}> · {contacts.length} records</span>
          <div className="mt-1" style={{ fontSize: 13 }}>
            {confidence === 'high' ? (
              <span style={{ color: T.success, fontWeight: 600, background: T.successBg, borderRadius: 999, padding: '2px 8px' }}>
                <i className="fa fa-check-circle mr-1" />Likely the same person
              </span>
            ) : confidence === 'medium' ? (
              <span style={{ color: T.warning, fontWeight: 600, background: T.warningBg, borderRadius: 999, padding: '2px 8px' }}>
                <i className="fa fa-map-marker-alt mr-1" />Possibly the same person — shares a location
              </span>
            ) : (
              <span style={{ color: T.textTertiary }}>Name only</span>
            )}
            {matchValue && (
              <span style={{ color: T.textTertiary }}>
                {'  '}Shares {matchType === 'card' ? 'a payment card' : matchType === 'phone' ? 'a phone number' : 'an email'} — {matchValue}
              </span>
            )}
          </div>
        </div>
        <button type="button" className="btn btn-setlist-secondary btn-sm" style={{ flexShrink: 0 }} onClick={onMerge}>
          <i className="fal fa-code-merge mr-2" />Review &amp; merge
        </button>
      </div>

      <table className="table table-sm mb-0 dup-table" style={{ color: T.textPrimary, tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          <col style={{ width: '30%' }} />
          <col style={{ width: '28%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: '24%' }} />
        </colgroup>
        <thead>
          <tr style={{ color: T.textTertiary, fontSize: 12 }}>
            <th style={{ border: 'none', fontWeight: 600, background: T.headerBg, padding: '8px 12px', borderRadius: '6px 0 0 6px' }}>Name</th>
            <th style={{ border: 'none', fontWeight: 600, background: T.headerBg, padding: '8px 12px' }}>Email</th>
            <th style={{ border: 'none', fontWeight: 600, background: T.headerBg, padding: '8px 12px' }}>Phone</th>
            <th style={{ border: 'none', fontWeight: 600, background: T.headerBg, padding: '8px 12px', borderRadius: '0 6px 6px 0' }}>Payment cards</th>
          </tr>
        </thead>
        <tbody>
          {contacts.map((c) => {
            const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.name || '—';
            const phone = c.human_phone || c.phone_number;
            const cards = (c.cards || []).map(formatCard);
            const emailMatch = matchType === 'email' && matchValue
              && [c.email, c.email_secondary_1, c.email_secondary_2].filter(Boolean)
                .some((e) => e.toLowerCase() === String(matchValue).toLowerCase());
            const matchDigits = matchType === 'phone' && matchValue ? String(matchValue).replace(/\D/g, '') : null;
            const phoneMatch = !!matchDigits
              && [c.phone_number, c.phone_secondary_1].filter(Boolean)
                .some((p) => String(p).replace(/\D/g, '') === matchDigits);
            const cellStyle = { border: 'none', verticalAlign: 'middle', padding: '10px 12px 10px 0' };
            const truncate = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
            return (
              <tr key={c.id}>
                <td style={cellStyle} data-label="Name">
                  <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 0 }}>
                    <CrmAvatar firstName={c.first_name} lastName={c.last_name} name={c.name} avatarUrl={c.avatar_thumb_url} size={32} className="team-avatar-sm" />
                    <span style={truncate} title={fullName}>{fullName}</span>
                  </div>
                </td>
                <td style={cellStyle} data-label="Email">
                  <div style={truncate} title={c.email || ''}>
                    <Highlight active={emailMatch}>{c.email || '—'}</Highlight>
                  </div>
                </td>
                <td style={cellStyle} data-label="Phone">
                  {phone ? <Highlight active={phoneMatch}>{phone}</Highlight> : <span style={{ color: T.textTertiary }}>—</span>}
                </td>
                <td style={cellStyle} data-label="Payment cards">
                  {cards.length === 0 ? (
                    <span style={{ color: T.textTertiary }}>—</span>
                  ) : (
                    cards.map((label) => (
                      <span key={label} style={{ marginRight: 10, whiteSpace: 'nowrap' }}>
                        <i className="fal fa-credit-card mr-1" style={{ color: T.textTertiary }} />
                        <Highlight active={matchType === 'card' && label === matchValue}>{label}</Highlight>
                      </span>
                    ))
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DuplicatesPage({ csrfToken, team }) {
  const [chip, setChip] = useState('likely');
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({});
  const [mergeContacts, setMergeContacts] = useState(null);
  const requestRef = useRef(0);

  // Chip counts are six grouped queries (a few seconds on big teams), so load
  // them in the background rather than blocking the initial page render. Also
  // refetched after a merge, since a merge changes every chip's count.
  const fetchCounts = useCallback(() => {
    axios.get(`/teams/${team.id}/crm/fans/duplicates.json`, { params: { counts: 1 } })
      .then((res) => setCounts((res.data && res.data.counts) || {}))
      .catch(() => {});
  }, [team && team.id]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  useEffect(() => {
    const requestId = (requestRef.current += 1);
    const delay = query ? 300 : 0;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        const response = await axios.get(`/teams/${team.id}/crm/fans/duplicates.json`, {
          params: { chip, page, q: query || undefined },
        });
        if (requestRef.current !== requestId) return;
        const data = response.data || {};
        setGroups(data.groups || []);
        // The server only sends total_groups on page 1 (it's unchanged across
        // page turns); keep the existing total when it's omitted.
        if (data.total_groups != null) setTotal(data.total_groups);
      } catch (e) {
        if (requestRef.current !== requestId) return;
        setError('Failed to load duplicates. Please try again.');
        setGroups([]);
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [team && team.id, csrfToken, chip, page, query]);

  const totalPages = Math.max(Math.ceil(total / PER_PAGE), 1);

  const handleMerged = (merged, removedIds) => {
    setGroups((prev) => {
      const next = prev.filter((g) => !g.contacts.some((c) => removedIds.includes(c.id)));
      setTotal((t) => Math.max(t - (prev.length - next.length), 0));
      return next;
    });
    setMergeContacts(null);
    fetchCounts();
  };

  const groupsPrefix = counts.all != null ? `${counts.all.toLocaleString()} groups of contacts share an identical name. ` : '';
  const subtitle = `${groupsPrefix}Merging combines a group into one contact — nothing is deleted, and every order and ticket is kept.`;

  return (
    <CrmPageLayout team={team} activeSidebarKey="contacts" mainTopMargin={32} title="Duplicate contacts" subtitle={subtitle}>
      {/* Search + chips */}
      <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 8 }}>
        <div style={{ position: 'relative', flex: '1 1 280px', minWidth: 240 }}>
          <i className="fal fa-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: T.textTertiary }} />
          <input
            type="text"
            className="form-control"
            placeholder="Search by name"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            style={{ paddingLeft: 34 }}
          />
        </div>
        <div className="d-flex flex-wrap" style={{ gap: 6 }}>
          {CHIPS.map(({ key, label }) => {
            const active = chip === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => { setChip(key); setPage(1); setQuery(''); }}
                style={{
                  border: `1px solid ${active ? 'var(--sl-border-primary)' : T.border}`,
                  background: active ? T.activeBg : T.cardBg,
                  color: active ? T.textPrimary : T.textSecondary,
                  borderRadius: 999,
                  padding: '5px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
                {counts[key] != null && <span style={{ color: T.textTertiary, marginLeft: 6 }}>{counts[key].toLocaleString()}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

      {loading && groups.length === 0 ? (
        <p style={{ color: T.textTertiary }}>Loading…</p>
      ) : (
        <>
          {groups.length === 0 && !error && (
            <p style={{ color: T.textTertiary }}>
              {query ? 'No duplicate groups match your search.' : 'No duplicate groups found.'}
            </p>
          )}

          {groups.map((group, index) => (
            <GroupCard
              key={`${chip}:${group.key}:${index}`}
              group={group}
              onMerge={() => setMergeContacts(group.contacts)}
            />
          ))}

          {groups.length > 0 && (
            <TeamPagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={total}
              pageSize={PER_PAGE}
              itemLabel="groups"
              onPageChange={(p) => { if (!loading && p >= 1 && p <= totalPages) setPage(p); }}
            />
          )}
        </>
      )}

      <div style={{ height: 100 }} aria-hidden="true" />

      {mergeContacts && (
        <MergeContactsModal
          contacts={mergeContacts}
          team={team}
          csrfToken={csrfToken}
          onMerged={handleMerged}
          onClose={() => setMergeContacts(null)}
        />
      )}
    </CrmPageLayout>
  );
}

export default DuplicatesPage;
